import { Box, Button, Paper, Typography, Alert } from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import SearchBar from '../../../components/SearchBar';
import AssignNewMachineTable from './AssignMachineTable';
import useStyles from './style';
import {
  useUpdateSubLocationMachineByIdMutation,
  useUnlinkMachineFromSublocationMutation,
} from '../../../services/Sublocation/SublocationService';
import { useLazyGetAssetResyncStatusQuery } from '../../../services/Machines/MachinesService';
import { isArrayWithLength } from '../../../utils/common-methods';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

const AssignNewMachine = ({ sublocationId, openAssignNew, machineList = [] }) => {
  const classes = useStyles();
  const [searchText, setSearchText] = useState('');
  const [rows, setRows] = useState([]);
  const [updateSublocationMachineApi] = useUpdateSubLocationMachineByIdMutation();
  const [unlinkMachineFromSublocation] = useUnlinkMachineFromSublocationMutation();
  const [getAssetResyncStatus] = useLazyGetAssetResyncStatusQuery();
  const [preSelectedRows, setPreSelectedRows] = useState({});
  const updatedBy = useSelector((state) => state.user?.userDetails?.id);
  const [resyncWarning, setResyncWarning] = useState('');
  const isResyncEnabled = process.env.REACT_APP_ENABLE_FEED_RESYNC === 'true';

  const searchHandler = (params) => {
    setSearchText(params);
  };

  const onReset = () => {
    setSearchText('');
  };

  useEffect(() => {
    if (isArrayWithLength(machineList)) {
      let preSelectedRow = {};
      machineList?.forEach((machine) => {
        preSelectedRow = { ...preSelectedRow, [machine?.id]: machine };
      });
      setRows({ ...preSelectedRow });
      setPreSelectedRows({ ...preSelectedRow });
    }
  }, []);

  const onRowDataChange = (rowId, field, value) => {
    setRows((prev) => {
      if (!prev[rowId]) return prev;
      return {
        ...prev,
        [rowId]: {
          ...prev[rowId],
          [field]: value,
        },
      };
    });
  };

  const onSelect = (row, checked) => {
    setRows((prev) => ({ ...prev, [row.id]: checked ? row : undefined })); // Store row object if checked, remove if unchecked
  };

  const onSaveHandler = async () => {
    setResyncWarning('');
    let apiData = [];
    let removedMachinesData = [];

    for (const key in preSelectedRows) {
      if (!rows[key]) {
        const prevRow = machineList.find((m) => m.assetId === key);
        if (prevRow) {
          removedMachinesData.push({
            ...prevRow,
          });
        }
      }
    }
    for (const key in rows) {
      const rowObj = rows[key];
      const updateMachineRemoved = Boolean(preSelectedRows[key] && !rowObj);
      if (updateMachineRemoved) {
        const prevRow = preSelectedRows[key];
        removedMachinesData.push({ ...prevRow, updatedBy });
      }
      if (rowObj && !updateMachineRemoved && !preSelectedRows[key]) {
        const formattedRow = {
          ...rowObj,
          inEffectFrom: rowObj.inEffectFrom ? dayjs(rowObj.inEffectFrom).format('YYYY-MM-DD') : undefined,
          inEffectTo: rowObj.inEffectTo ? dayjs(rowObj.inEffectTo).format('YYYY-MM-DD') : undefined,
          updatedBy: updatedBy,
        };
        apiData.push(formattedRow);
      }
    }

    // Check resync status for all affected assets
    if (isResyncEnabled) {
      const allAssetIds = [
        ...apiData.map((item) => item.id),
        ...removedMachinesData.map((item) => item.assetId || item.id),
      ].filter(Boolean);

      for (const assetId of allAssetIds) {
        try {
          const resyncResult = await getAssetResyncStatus(assetId).unwrap();
          if (resyncResult?.data?.resync) {
            setResyncWarning(
              'A resync is currently in progress for this asset. Please try again after resync is complete.',
            );
            return;
          }
        } catch {
          // If API fails, proceed
        }
      }
    }

    if (removedMachinesData.length !== 0) {
      unlinkMachineFromSublocation({
        id: sublocationId,
        assets: removedMachinesData,
      });
    }
    if (apiData?.length !== 0) {
      updateSublocationMachineApi({
        id: sublocationId,
        assets: apiData,
      }).then((response) => {
        if (response.data) {
          openAssignNew(false);
        }
      });
    }
    openAssignNew(false);
  };

  const onCancelHandler = () => {
    openAssignNew(false);
  };

  const isSaveDisabled = useMemo(() => {
    let currentRowSelection = { ...rows };
    let trueValuesOfRowSelection = {};
    Object.keys(currentRowSelection)?.map((item) => {
      if (currentRowSelection[item] || (preSelectedRows[item] && !currentRowSelection[item])) {
        trueValuesOfRowSelection = { ...trueValuesOfRowSelection, [item]: currentRowSelection[item] };
      }
    });
    return Boolean(JSON.stringify(trueValuesOfRowSelection) === JSON.stringify(preSelectedRows));
  }, [rows]);

  return (
    <Box component={Paper} className={classes.assignNewMachineBox}>
      <Box className={classes.assignNewMachineTitle}>
        <Typography variant="h2">Assign New Machine</Typography>
      </Box>
      <Box className={classes.machineSearch}>
        <SearchBar onSearch={searchHandler} onReset={onReset} placeholder="Search machines" />
      </Box>
      {resyncWarning && (
        <Box sx={{ m: 2 }}>
          <Alert severity="warning">{resyncWarning}</Alert>
        </Box>
      )}
      <AssignNewMachineTable
        sublocationId={sublocationId}
        selectedRows={rows}
        searchParams={searchText}
        onSelect={onSelect}
        onRowDataChange={onRowDataChange}
      />
      <Box className={classes.btnWrap}>
        <Button variant="contained" onClick={onSaveHandler} disabled={isSaveDisabled}>
          SAVE
        </Button>
        <Button onClick={onCancelHandler}>CANCEL</Button>
      </Box>
    </Box>
  );
};

export default AssignNewMachine;
